import { Component } from '@angular/core';
import { OrganizationContractService, OwnedContract } from '@shared/services/contract.service';
import { BehaviorSubject, mergeMap, Observable, of } from 'rxjs';
import { emptyPage, map as mapPage, SpringPage } from '@shared/page';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Options } from 'highcharts';
import { AuthService } from '@shared/services/auth.service';
import moment from 'moment';
import { formatHumanReadableDate } from '@shared/utils/data-utils';
import { ContractChartService } from '@shared/services/contract-chart.service';
import { MyContractsHeaderComponent } from '@shared/components/my-contracts-header/my-contracts-header.component';
import { GraphComponent } from '@shared/components/highcharts/graph/graph.component';
import { ContractDetailComponent } from './contract-detail/contract-detail.component';
import { TranslatePipe } from '@ngx-translate/core';
import { TimedBucket } from '@shared/services/abstract-contract.service';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NgxPaginationModule, PaginationInstance } from 'ngx-pagination';

@Component({
  selector: 'app-my-contracts',
  templateUrl: './my-contracts.component.html',
  styleUrl: './my-contracts.component.scss',
  imports: [
    MyContractsHeaderComponent,
    GraphComponent,
    ContractDetailComponent,
    TranslatePipe,
    ReactiveFormsModule,
    NgxPaginationModule,
  ],
})
export class MyContractsComponent {
  public selectedContract: Contract | undefined = undefined;
  protected contracts: SpringPage<Contract> = emptyPage<Contract>();
  protected readonly searchControl = new FormControl('', { nonNullable: true });
  protected loading = false;
  protected paginationConfig: PaginationInstance = {
    id: 'contracts',
    itemsPerPage: 20,
    currentPage: 1,
    totalItems: 0,
  };
  private firstLoad = true;
  private readonly state$ = new BehaviorSubject<{ filter: string; page: number }>({ filter: '', page: 0 });

  constructor(
    private userContractService: OrganizationContractService,
    private auth: AuthService,
    private contractChartService: ContractChartService,
  ) {
    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe((filter) => this.state$.next({ filter, page: 0 }));

    this.state$
      .pipe(
        switchMap(({ filter, page }) => {
          this.loading = true;
          return this.getContracts(filter, page);
        }),
        takeUntilDestroyed(),
      )
      .subscribe((page) => {
        this.loading = false;
        this.contracts = page;
        this.paginationConfig = {
          ...this.paginationConfig,
          currentPage: this.state$.value.page + 1,
          totalItems: page.totalElements,
        };
        if (this.firstLoad && page.totalElements === 1) {
          this.selectedContract = page.content[0];
        }
        this.firstLoad = false;
      });
  }

  public getCurrentMonthData(contract: OwnedContract, bucket: TimedBucket | undefined): CurrentMonthData {
    const date = moment(bucket?.date ?? undefined).startOf('month');
    const buckets = bucket ? [bucket] : [];
    return {
      currentMonth: {
        from: formatHumanReadableDate(date.clone(), this.auth.getCurrentLanguage(), 'date'),
        to: formatHumanReadableDate(date.clone().add(1, 'month'), this.auth.getCurrentLanguage(), 'date'),
      },
      requestsData: this.formatDataTrafic(buckets, contract),
      bandwidthData: this.formatBandwidthData(buckets, contract),
      activeSiteData: this.formatNbSitesData(buckets, contract),
    };
  }

  protected onPageChange(page: number) {
    this.state$.next({ filter: this.searchControl.value, page: page - 1 });
  }

  public selectContract(contract: Contract, event?: MouseEvent) {
    event?.stopPropagation();
    this.selectedContract = contract;
  }

  private getContracts(filter: string, page: number): Observable<SpringPage<Contract>> {
    return this.userContractService
      .getOwnedContracts({ type: [], filter }, { number: page, size: this.paginationConfig.itemsPerPage })
      .pipe(
        mergeMap((springPage) => {
          if (!springPage.content.length) {
            return of(emptyPage<Contract>());
          }
          return this.userContractService.getInvoicingStats(springPage.content.map((c) => c.number)).pipe(
            map((stats) =>
              mapPage(springPage, (contract: OwnedContract) => ({
                ...contract,
                ...this.getCurrentMonthData(contract, stats[contract.number]?.currentMonth),
                nbTotalSites: stats[contract.number]?.nbTotalSites || 0,
                nbInvoicedSites: stats[contract.number]?.nbInvoicedSites || 0,
                nbInvoicedClusters: stats[contract.number]?.nbInvoicedClusters || 0,
                nbOrganizations: stats[contract.number]?.nbOrganizations || 0,
              })),
            ),
          );
        }),
      );
  }

  private getAxes(): Pick<Options, 'xAxis' | 'yAxis'> {
    return {
      xAxis: {
        labels: {
          enabled: false,
        },
        tickLength: 0,
      },
      yAxis: {
        labels: {
          enabled: false,
        },
      },
    };
  }

  private formatDataTrafic(buckets: Array<TimedBucket>, contract: OwnedContract): Options {
    return this.contractChartService.formaTrafficData(contract, buckets, 'bar', {
      ...this.getAxes(),
    });
  }

  private formatBandwidthData(buckets: Array<TimedBucket>, contract: OwnedContract): Options {
    return this.contractChartService.formatBandwidthData(contract, buckets, 'bar', {
      ...this.getAxes(),
    });
  }

  private formatNbSitesData(buckets: Array<TimedBucket>, contract: OwnedContract): Options {
    return this.contractChartService.formatNbSitesData(contract, buckets, 'bar', {
      ...this.getAxes(),
    });
  }
}

export interface Contract extends OwnedContract, CurrentMonthData {
  nbTotalSites: number;
  nbInvoicedSites: number;
  nbInvoicedClusters: number;
  nbOrganizations: number;
}

export type CurrentMonthData = {
  currentMonth: {
    from: string;
    to: string;
  };
  requestsData: Options;
  bandwidthData: Options;
  activeSiteData: Options;
};
